<template>
  <div
    :class="{
      'select-input-fi': true,
      'select-input-fi--label': label,
      'select-input-fi--disabled': disabled,
      'select-input-fi--error': isValidationError,
      'select-input-fi--selected': internalValue !== null,
    }"
  >
    <div class="select-input-fi__container">
      <label-fi
        class="select-input-fi__label"
        :label="label"
        :mandatory="required"
      />
      <select-fi
        class="select-input-fi__select"
        :name="nameOrSelection"
        :multiselect="multiselect"
        :elements="elements"
        :open="open"
        :small="small"
        :large="large"
        :large-from="largeFrom"
        :light="light"
        :transparent="transparent"
        :force-open-top="forceOpenTop"
        :force-open-bottom="forceOpenBottom"
        :has-error="isValidationError"
        :disabled="disabled"
        :required="required"
        :data-test-2="dataTest2"
        options-overlap
        mobile-chevron
        no-mobile-selection
        @change="onChange"
        @toggle-open="onToggleOpen"
        @clear-selection="$emit('clear-selection')"
      />
    </div>

    <div
      v-if="isValidationError"
      class="select-input-fi__error"
    >
      {{ errorText }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import LabelFi from '@/components/atoms/label-fi/LabelFi.vue';
import SelectFi from '@/components/atoms/select-fi/SelectFi.vue';
import { type SizeBreakpoint, useBreakpoints } from '@/composables/breakpoint';

defineOptions({ name: 'SelectInputFi' });

interface Props {
  value: string | null;
  label?: string | null;
  name?: string;
  options?: any[] | null;
  multiselect?: boolean;
  small?: boolean;
  large?: boolean;
  largeFrom?: SizeBreakpoint;
  light?: boolean;
  transparent?: boolean;
  forceOpenTop?: boolean;
  forceOpenBottom?: boolean;
  rules?: any[];
  required?: boolean;
  disabled?: boolean;
  dataTest2?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  label: null,
  name: '',
  options: null,
  multiselect: false,
  small: false,
  large: false,
  largeFrom: undefined,
  light: false,
  transparent: false,
  forceOpenTop: false,
  forceOpenBottom: false,
  rules: () => [],
  required: false,
  disabled: false,
  dataTest2: null,
});

const emit = defineEmits<{
  'clear-selection': [];
  input: [value: any, option: any];
  open: [value: boolean];
}>();

const { isSM } = useBreakpoints();

const open = ref(false);
const valid = ref(false);
const errorBucket = ref<any[]>([]);
const errorText = ref<string | null>(null);
const internalValue = ref<string | null>(null);

const nameOrSelection = computed(() => {
  if (internalValue.value && (!open.value || isSM.value)) {
    const selection = props.options?.find((option) => option.value === internalValue.value);
    if (selection) {
      return selection.name;
    }
  }
  return props.name;
});

const elements = computed(() => {
  if (props.multiselect) {
    return props.options;
  }

  return props.options?.map((option) => ({
    ...option,
    selected: option.value === internalValue.value,
  }));
});

const isValidationError = computed(() => !valid.value && props.rules && props.rules.length > 0);

const validate = () => {
  const value = props.value;
  const bucket: any[] = [];
  props.rules.forEach((rule) => {
    const result = typeof rule === 'function' ? rule(value) : rule;

    if (result === false || typeof result === 'string') {
      bucket.push(result || '');
    } else if (typeof result !== 'boolean') {
      console.error(`Rules should return a string or boolean, received '${typeof result}' instead`);
    }
  });
  errorBucket.value = bucket;
  errorText.value = bucket[0] ?? null;
  valid.value = bucket.length === 0;

  return valid.value;
};

watch(
  () => props.value,
  (newValue) => {
    internalValue.value = newValue;
    validate();
  },
  { immediate: true },
);

const onToggleOpen = () => {
  open.value = !open.value;

  if (!open.value && props.rules && props.rules.length > 0) {
    validate();
  }

  emit('open', open.value);
};

const close = () => {
  if (open.value) {
    onToggleOpen();
  }
};

defineExpose({ validate, valid });

const onChange = ({ value, element }: { value: any; element: any }) => {
  if (!value && !props.multiselect) {
    element.selected = true;
    close();
    return;
  }
  const origOption = props.options?.find((option) => option.value === element.value);
  if (origOption) {
    if (props.multiselect) {
      origOption.selected = value;
      emit('input', origOption.value, origOption);
    } else if (origOption.value !== internalValue.value) {
      emit('input', origOption.value, origOption);
      internalValue.value = origOption.value;
      if (open.value) {
        onToggleOpen();
      }
    }
  } else {
    console.error('Could not emit change, option not found', value);
  }
};
</script>

<style scoped src="./select-input-fi" />

<style src="./select-input-fi-unscoped" />
